let paddleInitialized = false;
let paddleScriptLoaded = false;
let scriptLoadPromise = null;

// Callbacks for the in-flight checkout session. Paddle.js v2 routes all
// checkout events through the single eventCallback registered at Initialize();
// we fan them back out to the caller that opened this checkout.
let checkoutCallbacks = {};

/**
 * Load the Paddle.js v2 script exactly once.
 */
const loadPaddleScript = () => {
  if (paddleScriptLoaded || window.Paddle) {
    paddleScriptLoaded = true;
    return Promise.resolve();
  }
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.async = true;
    script.onload = () => {
      paddleScriptLoaded = true;
      resolve();
    };
    script.onerror = () => reject(new Error('Failed to load Paddle.js script'));
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
};

/**
 * Initialize Paddle.js with the client token, environment, and (optionally) the
 * Paddle customer id for Retain.
 *
 * `pwCustomer` wires Paddle Retain (dunning / churn recovery) to the logged-in
 * customer — without it Retain can't attribute the session, so we pass it
 * whenever we know the customer's Paddle id.
 *
 * @param {string} clientToken
 * @param {string} environment  'sandbox' | 'production'
 * @param {{ id?: string }} [pwCustomer]  Paddle customer, e.g. { id: 'ctm_...' }
 */
export const initializePaddle = async (clientToken, environment = 'sandbox', pwCustomer = null) => {
  if (paddleInitialized && window.Paddle) {
    return window.Paddle;
  }

  await loadPaddleScript();
  if (!window.Paddle) {
    throw new Error('Paddle.js not available after script load');
  }

  // Only point at sandbox on an explicit backend signal — never in production.
  if (environment === 'sandbox') {
    window.Paddle.Environment.set('sandbox');
  }

  const initOptions = {
    token: clientToken,
    eventCallback: (event) => {
      if (event.name === 'checkout.completed') {
        checkoutCallbacks.onSuccess?.(event.data);
      } else if (event.name === 'checkout.closed') {
        checkoutCallbacks.onClose?.();
      } else if (event.name === 'checkout.error') {
        checkoutCallbacks.onError?.(event.data);
      }
    }
  };

  // Attach the customer for Retain when available (id, or full pwCustomer obj).
  if (pwCustomer && pwCustomer.id) {
    initOptions.pwCustomer = { id: pwCustomer.id };
  }

  window.Paddle.Initialize(initOptions);
  paddleInitialized = true;
  return window.Paddle;
};

/**
 * Open the Paddle checkout overlay for a backend-created transaction.
 *
 * @param {Object} options
 * @param {string} options.transactionId  transaction id from the backend
 * @param {Function} [options.onSuccess]
 * @param {Function} [options.onClose]
 * @param {Function} [options.onError]
 * @param {string} [options.theme]  'light' | 'dark' (default 'light')
 */
export const openPaddleCheckout = ({ transactionId, onSuccess, onClose, onError, theme = 'light' }) => {
  if (!window.Paddle) {
    throw new Error('Paddle.js not initialized. Call initializePaddle() first.');
  }

  checkoutCallbacks = { onSuccess, onClose, onError };

  window.Paddle.Checkout.open({
    transactionId,
    settings: {
      displayMode: 'overlay',
      theme,
      locale: 'en'
    }
  });
};

/**
 * Open Paddle checkout INLINE — mounted into a container in your own form
 * instead of the popup overlay. Paddle renders its (PCI-compliant) card fields
 * as iframes inside the `frameTarget` element, so the surrounding layout and
 * branding are yours — the same model as Stripe Elements.
 *
 * The container element must:
 *   - carry the class passed as `frameTarget` (class only, no leading dot)
 *   - already exist in the DOM when this is called
 *   - have a width (Paddle sizes the frame to it)
 *
 * @param {Object} options
 * @param {string} options.transactionId  transaction id from the backend
 * @param {string} options.frameTarget    class name of the mount container
 * @param {Function} [options.onSuccess]
 * @param {Function} [options.onClose]
 * @param {Function} [options.onError]
 * @param {string} [options.theme]  'light' | 'dark' (default 'light')
 * @param {number} [options.frameInitialHeight]  initial px height (default 450)
 */
export const openPaddleInlineCheckout = ({
  transactionId,
  frameTarget,
  onSuccess,
  onClose,
  onError,
  theme = 'light',
  frameInitialHeight = 450
}) => {
  if (!window.Paddle) {
    throw new Error('Paddle.js not initialized. Call initializePaddle() first.');
  }
  if (!frameTarget) {
    throw new Error('openPaddleInlineCheckout requires a frameTarget class name.');
  }

  checkoutCallbacks = { onSuccess, onClose, onError };

  window.Paddle.Checkout.open({
    transactionId,
    settings: {
      displayMode: 'inline',
      frameTarget,
      frameInitialHeight,
      // Transparent + borderless so the frame blends into the host form; the
      // container element supplies the visual chrome. Paddle enforces a 312px
      // hard floor on the frame; we set 400px so the form isn't cramped.
      frameStyle: 'width:100%; min-width:400px; background-color:transparent; border:none;',
      theme,
      locale: 'en'
    }
  });
};

/**
 * Whether Paddle.js is loaded and initialized.
 */
export const isPaddleReady = () => paddleInitialized && !!window.Paddle;
